import React, { useState, useEffect } from 'react';
import { func, bool } from 'prop-types';
import ModalBackground from '@alltrails/shared/components/modals/ModalBackground';
import useMultiStepModal from '@alltrails/shared/hooks/useMultiStepModal';
import ManagedMembersModal from '@alltrails/communityapp/ManagedMembersModal';
import '@alltrails/communityapp/dist/ManagedMembersModal.css';
import IndividualPrivacyModal from '../IndividualPrivacyModal';
import useScreenLock from '../../../../hooks/useScreenLock';
import InitialPrivacyPolicyModal from '../InitialPrivacyPolicyModal';
import PrivacyPolicyPropType from '../../../../proptypes/PrivacyPolicyPropType';
import { policyToRadioForm, PRIVACY_POLICY_KEYS_MAP } from '../../../../utils/privacy_policy_helpers';
import { contextPropTypes } from '../../../../proptypes/shared';
import CustomProvider from 'components/CustomProvider';

const GeneralPrivacyPolicyModal = ({ context, initialPrivacyPolicy, closeModal, handleSave, loading }) => {
  const [privacyPolicy, updatePrivacyPolicy] = useState(initialPrivacyPolicy);
  const [individualPrivacyModalProps, setIndividualPrivacyModalProps] = useState({});
  const [activeSubject, setActiveSubject] = useState('');
  const [applyBulk, toggleApplyBulk] = useState(false);
  const [managedMembersListKey, setManagedMembersListKey] = useState('');
  const { mountedModal, linkedState, goBack, navigateTo, handleCloseModal, handleOnTransitionEnd } = useMultiStepModal('baseModal', closeModal);
  useScreenLock();

  useEffect(() => {
    updatePrivacyPolicy(initialPrivacyPolicy);
  }, [initialPrivacyPolicy]);

  const onPrivacyClick = (subject, visibility) => {
    setActiveSubject(subject);
    setIndividualPrivacyModalProps(policyToRadioForm(subject, visibility));
    navigateTo('individualPrivacyModal');
  };

  const onManagedMembersListClick = listKey => {
    setManagedMembersListKey(listKey);
    navigateTo('managedMembersModal');
  };

  const handleAcceptCallback = value => {
    const [outerKey, resultKey] = PRIVACY_POLICY_KEYS_MAP[activeSubject];
    // for "yes or no" radio buttons the nesting is one level up
    updatePrivacyPolicy(prevState => {
      const payload =
        resultKey === activeSubject
          ? { ...prevState[outerKey], [activeSubject]: value }
          : { ...prevState[outerKey], [activeSubject]: { ...prevState[outerKey][activeSubject], [resultKey]: value } };

      return {
        ...prevState,
        [outerKey]: payload
      };
    });
  };

  const baseModal = privacyPolicy && (
    <InitialPrivacyPolicyModal
      goBack={goBack}
      privacyPolicy={privacyPolicy}
      isMounted={linkedState.key === mountedModal}
      handleOnTransitionEnd={handleOnTransitionEnd}
      onPrivacyClick={onPrivacyClick}
      onManagedMembersListClick={onManagedMembersListClick}
      handleCloseModal={handleCloseModal}
      loading={loading}
      handleSave={handleSave}
      applyBulk={applyBulk}
      toggleApplyBulk={toggleApplyBulk}
    />
  );

  const individualPrivacyModal = (
    <IndividualPrivacyModal
      goBack={goBack}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      acceptCallback={handleAcceptCallback}
      {...individualPrivacyModalProps}
    />
  );

  const managedMembersModal = (
    <CustomProvider>
      <ManagedMembersModal
        context={context}
        listKey={managedMembersListKey}
        isMounted={linkedState.key === mountedModal}
        goBack={goBack}
        onTransitionEnd={handleOnTransitionEnd}
      />
    </CustomProvider>
  );

  const activeModalOptions = {
    baseModal,
    individualPrivacyModal,
    managedMembersModal
  };

  return (
    <ModalBackground closeModal={handleCloseModal} goBack={goBack}>
      {activeModalOptions[linkedState.key]}
    </ModalBackground>
  );
};

GeneralPrivacyPolicyModal.propTypes = {
  context: contextPropTypes.isRequired,
  initialPrivacyPolicy: PrivacyPolicyPropType,
  closeModal: func.isRequired,
  handleSave: func.isRequired,
  loading: bool
};

GeneralPrivacyPolicyModal.defaultProps = {
  loading: false,
  initialPrivacyPolicy: null
};

export default GeneralPrivacyPolicyModal;
